import { fetchWithError } from "../../utils/fetchWithError.ts";
import { useCallback, useEffect, useState } from "react";
import { TabPanel, TabPanelProps } from "../../components/TabPanel.tsx";
import { IMessage, ListResult } from "@ids/shared";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography
} from "@mui/material";
import { DoneAll, Mail, MarkEmailRead } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { InboxListItem } from "../../components/InboxListItem.tsx";

const PAGE_SIZE = 10;

interface MessagesTabProps extends TabPanelProps {
    onUnreadCountChange?: (count: number) => void;
}

export const MessagesTab = (props: MessagesTabProps) => {
    const { onUnreadCountChange, value, index } = props;
    const [unread, setUnread] = useState<IMessage[]>([]);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [unreadOffset, setUnreadOffset] = useState(0);
    const [read, setRead] = useState<IMessage[]>([]);
    const [readTotal, setReadTotal] = useState(0);
    const [readOffset, setReadOffset] = useState(0);
    const [showRead, setShowRead] = useState(false);
    const [loadingUnread, setLoadingUnread] = useState(false);
    const [loadingRead, setLoadingRead] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const loadUnread = useCallback(
        async (offset = 0, append = false) => {
            setLoadingUnread(true);
            try {
                const params = new URLSearchParams({
                    read: "false",
                    limit: String(PAGE_SIZE),
                    offset: String(offset)
                });
                const result = await fetchWithError<ListResult<IMessage>>(
                    `/session/messages?${params.toString()}`,
                    { credentials: "include" }
                );
                setUnread((prev) =>
                    append ? [...prev, ...result.data] : result.data
                );
                setUnreadTotal(result.total);
                setUnreadOffset(offset + result.data.length);
                if (!append) {
                    onUnreadCountChange?.(result.total);
                }
            } catch (error) {
                console.error("Error loading unread messages:", error);
            } finally {
                setLoadingUnread(false);
            }
        },
        [onUnreadCountChange]
    );

    const loadRead = useCallback(async (offset = 0, append = false) => {
        setLoadingRead(true);
        try {
            const params = new URLSearchParams({
                read: "true",
                limit: String(PAGE_SIZE),
                offset: String(offset)
            });
            const result = await fetchWithError<ListResult<IMessage>>(
                `/session/messages?${params.toString()}`,
                { credentials: "include" }
            );
            setRead((prev) =>
                append ? [...prev, ...result.data] : result.data
            );
            setReadTotal(result.total);
            setReadOffset(offset + result.data.length);
        } catch (error) {
            console.error("Error loading read messages:", error);
        } finally {
            setLoadingRead(false);
        }
    }, []);

    useEffect(() => {
        loadUnread(0, false);
    }, [loadUnread]);

    const markAsRead = async (id: string) => {
        try {
            await fetchWithError(`/session/messages/${id}/read`, {
                method: "PUT",
                credentials: "include"
            });
            setUnread((prev) => prev.filter((m) => m.id !== id));
            setUnreadTotal((prev) => {
                const next = Math.max(0, prev - 1);
                onUnreadCountChange?.(next);
                return next;
            });
            enqueueSnackbar("Nachricht als gelesen markiert", {
                variant: "info",
                autoHideDuration: 2500
            });
            if (showRead) {
                loadRead(0, false);
            }
        } catch (error) {
            console.error("Error marking message as read:", error);
            enqueueSnackbar("Nachricht konnte nicht markiert werden", {
                variant: "error"
            });
        }
    };

    const markAllAsRead = async () => {
        try {
            await fetchWithError("/session/messages/read-all", {
                method: "PUT",
                credentials: "include"
            });
            setUnread([]);
            setUnreadTotal(0);
            setUnreadOffset(0);
            onUnreadCountChange?.(0);
            enqueueSnackbar("Alle Nachrichten als gelesen markiert", {
                variant: "info",
                autoHideDuration: 2500
            });
            if (showRead) {
                loadRead(0, false);
            }
        } catch (error) {
            console.error("Error marking all messages as read:", error);
            enqueueSnackbar("Nachrichten konnten nicht markiert werden", {
                variant: "error"
            });
        }
    };

    const handleShowRead = () => {
        setShowRead(true);
        loadRead(0, false);
    };

    const formatDate = (date: Date | string) => {
        return new Intl.DateTimeFormat("de-DE", {
            dateStyle: "medium",
            timeStyle: "short"
        }).format(new Date(date));
    };

    const getSenderName = (message: IMessage) => {
        const name = message.clientApp?.name;
        if (name === "Kontoverwaltung" || name === "tintoa") {
            return "tintoa";
        }
        return name ?? "Anwendung";
    };

    const renderMessageItem = (message: IMessage, unreadItem: boolean) => (
        <InboxListItem
            key={message.id}
            app={message.clientApp}
            sourceLabel={getSenderName(message)}
            title={message.title}
            body={message.content}
            footer={message.footer}
            link={message.link}
            linkTitle={message.linkTitle}
            date={formatDate(message.createdAt)}
            emphasized={unreadItem}
            dimmed={!unreadItem}
            actions={
                unreadItem ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MarkEmailRead />}
                        onClick={() => markAsRead(message.id)}
                    >
                        Als gelesen markieren
                    </Button>
                ) : undefined
            }
        />
    );

    const emptyUnreadState = (
        <Card variant="subtle" sx={{ borderRadius: { xs: 0, sm: 2 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
                <Mail
                    sx={{ color: "text.secondary", fontSize: 64, mb: 2 }}
                />
                <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                >
                    Keine ungelesenen Nachrichten
                </Typography>
                <Typography color="text.secondary">
                    Du hast derzeit keine neuen Nachrichten.
                </Typography>
            </CardContent>
        </Card>
    );

    return (
        <TabPanel value={value} index={index}>
            <Box sx={{ mb: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 1
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Ungelesene Nachrichten
                    </Typography>
                    {unreadTotal > 0 && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<DoneAll />}
                            onClick={markAllAsRead}
                        >
                            Alle als gelesen
                        </Button>
                    )}
                </Box>
            </Box>

            {loadingUnread && unread.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 300
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2}>
                    {unread.length > 0
                        ? unread.map((message) =>
                              renderMessageItem(message, true)
                          )
                        : emptyUnreadState}
                </Stack>
            )}

            {unreadOffset < unreadTotal && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Button
                        variant="outlined"
                        disabled={loadingUnread}
                        onClick={() => loadUnread(unreadOffset, true)}
                    >
                        {loadingUnread
                            ? "Laden…"
                            : "Weitere ungelesene laden"}
                    </Button>
                </Box>
            )}

            <Box sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Gelesene Nachrichten
                </Typography>
                {!showRead ? (
                    <Button variant="outlined" onClick={handleShowRead}>
                        Gelesene Nachrichten laden
                    </Button>
                ) : loadingRead && read.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 4
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Stack spacing={2}>
                            {read.length > 0 ? (
                                read.map((message) =>
                                    renderMessageItem(message, false)
                                )
                            ) : (
                                <Card
                                    variant="subtle"
                                    sx={{ borderRadius: { xs: 0, sm: 2 } }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            textAlign: "center"
                                        }}
                                    >
                                        <Typography color="text.secondary">
                                            Keine gelesenen Nachrichten.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                        {readOffset < readTotal && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mt: 3
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    disabled={loadingRead}
                                    onClick={() => loadRead(readOffset, true)}
                                >
                                    {loadingRead
                                        ? "Laden…"
                                        : "Weitere gelesene Nachrichten laden"}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </TabPanel>
    );
};
